import SearchSelect from 'elements/searchSelect/SearchSelect';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectSelectedInstrument,
  selectInstrumentPriceBars,
  selectInstrumentPriceBarsLoading,
  selectInstrumentsOptions,
  selectSelectedInstrumentOption,
} from 'store/entities/market/selectors';
import { setSelectedInstrument } from 'store/entities/market/slice';
import { subscribeInstrumentPrice, getInstrumentPriceBars } from 'store/entities/market/thunks';
import { subscribeOrderbook } from 'store/entities/market/orderbook/thunks';

import ChartRangeSwitcher from './chartRangeSwitcher';
import ChartTabContent from './chartTab';
import OrderbookTabContent from './orderBookTab';
import InstrumentPrice from './instrumentPrice';

import { ChartRangeOptionT, SelectOptionT, ChainType } from 'types';

import { options } from './chartRangeSwitcher/switcherOptions';

import { ReactComponent as GraphIcon } from 'assets/images/graph.svg';
import { ReactComponent as OrderbookIcon } from 'assets/images/orderbook.svg';

import './MainMarketInfo.scss';
import { ecosystem } from 'utils/smartcontract/isMainnet';

enum Tabs {
  Graph = 'graph',
  Orderbook = 'orderbook',
}

const MainMarketInfo = () => {
  const dispatch = useDispatch();
  const currentInstrument = useSelector(selectSelectedInstrument);
  const currentInstrumentOption = useSelector(selectSelectedInstrumentOption);
  const instrumentPriceBars = useSelector(selectInstrumentPriceBars);
  const isInstrumentPriceBarsLoading = useSelector(selectInstrumentPriceBarsLoading);
  const instrumentsOptions = useSelector(selectInstrumentsOptions);

  const [currentTab, setCurrentTab] = useState(Tabs.Orderbook);

  const defaultChartOption = options[0];
  const [selectedChartOption, setSelectedChartOption] = useState(defaultChartOption);

  useEffect(() => {
    if (currentInstrument) {
      const { instrument_id } = currentInstrument;
      const { periodicity, limit } = selectedChartOption;

      dispatch(subscribeInstrumentPrice(instrument_id));
      dispatch(subscribeOrderbook(instrument_id));
      dispatch(
        getInstrumentPriceBars({
          instrument_id,
          periodicity,
          limit,
        })
      );
    }
  }, [currentInstrument]);

  const chartRangeHandler = (option: ChartRangeOptionT) => {
    setSelectedChartOption(option);

    if (currentInstrument) {
      const { periodicity, limit } = option;

      dispatch(
        getInstrumentPriceBars({
          instrument_id: currentInstrument.instrument_id,
          periodicity,
          limit,
        })
      );
    }
  };

  const selectInstrumentHandler = (instrumentOption: SelectOptionT) => {
    const localStore = localStorage.getItem('store') || '{}';
    localStorage.setItem(
      'store',
      JSON.stringify({
        ...JSON.parse(localStore),
        currentInstrument: instrumentOption.value,
      })
    );

    dispatch(setSelectedInstrument(instrumentOption.value));
  };

  return (
    <div className="main-market-info">
      <div className="main-market-info-header">
        <div className="main-market-info-header-coll-1">
          <SearchSelect
            onChange={(o: SelectOptionT) => selectInstrumentHandler(o)}
            name={'instrument-selector'}
            value={currentInstrumentOption}
            placeholder={'Select pair'}
            options={instrumentsOptions}
          />
          {ecosystem === ChainType.Testnet ? (
            <a
              className="main-market-info-feedback"
              href="https://form.asana.com/?k=feiNfXHdNIFRYdKhk8BSjg&d=144088246915329"
              target="_blank"
            >
              {_t('Feedback Form', 'MARKET_INFO.FEEDBACK_FORM')}
            </a>
          ) : null}
        </div>
        <div className="main-market-info-header-coll-2">
          {ecosystem === ChainType.Testnet ? (
            <a
              className="main-market-info-faucet-btn"
              href="https://faucet.avax.network/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t('Faucet', 'MARKET_INFO.FAUCET')}
            </a>
          ) : (
            ''
          )}
          <div
            className={`main-market-info-graph-btn ${currentTab === Tabs.Graph ? 'active' : ''}`}
            onClick={() => setCurrentTab(Tabs.Graph)}
          >
            <span>
              <GraphIcon />
            </span>
          </div>
          <div
            className={`main-market-info-orderbook-btn ${currentTab === Tabs.Orderbook ? 'active' : ''}`}
            onClick={() => setCurrentTab(Tabs.Orderbook)}
          >
            <span>
              <OrderbookIcon />
            </span>
          </div>
        </div>
      </div>

      <div className="main-market-info-content">
        <div className="main-market-info-content-price">
          <div className="price-block">
            <InstrumentPrice decimals={currentInstrument?.price_decimals} />
          </div>

          {currentTab === Tabs.Graph && (
            <ChartRangeSwitcher selectedOption={selectedChartOption} setSelectedOption={chartRangeHandler} />
          )}
        </div>

        {currentTab === Tabs.Graph ? (
          <ChartTabContent
            selectedRange={selectedChartOption}
            instrumentPriceBars={instrumentPriceBars}
            isLoading={isInstrumentPriceBarsLoading}
          />
        ) : (
          <OrderbookTabContent decimal={currentInstrument?.price_decimals || 2} />
        )}
      </div>
    </div>
  );
};

export default MainMarketInfo;
